import React, { useState } from "react";
import useSEO from '../hooks/useSEO';
import NavBar from "../components/Navbar/NavBar";
import Clients from "../components/Clients";
import CompanyLogo from "../images/logo.svg";
import { Shield, Book, AlertCircle, X } from 'lucide-react';

const TabContent = {
    privacyPolicy: `
    <strong>Effective Date:</strong> 1st August, 2024
    <p>Yaniva Ventures Private Limited ("Company," "we," "our," or "us") respects your privacy and is committed to protecting it through this Privacy Policy.</p>
    <h3 class="font-semibold mt-4">1. Information We Collect</h3>
    <ul class="list-disc list-inside text-slate-700">
      <li><strong>Personal Information:</strong> Name, email address, phone number, Demat account details, and other identifiers you provide during registration or investment processes.</li>
      <li><strong>Financial Information:</strong> Payment details for corporate consulting and advisory transactions.</li>
      <li><strong>Usage Data:</strong> Information about your browsing activity, IP address, and device identifiers.</li>
    </ul>
    <h3 class="font-semibold mt-4">2. How We Use Your Information</h3>
    <ul class="list-disc list-inside text-slate-700">
      <li>Facilitating investments and transactions.</li>
      <li>Providing personalized recommendations and financial insights.</li>
      <li>Enhancing user experience on the website.</li>
      <li>Complying with legal and regulatory requirements.</li>
    </ul>
    <h3 class="font-semibold mt-4">3. Sharing Your Information</h3>
    <ul class="list-disc list-inside text-slate-700">
      <li>Service providers and partners to facilitate transactions.</li>
      <li>Regulatory authorities for compliance purposes.</li>
      <li>As required by law or to protect our rights.</li>
    </ul>
    <h3 class="font-semibold mt-4">4. Cookies and Tracking Technologies</h3>
    <ul class="list-disc list-inside text-slate-700">
      <li>Improve website functionality.</li>
      <li>Track website usage and analyze trends.</li>
    </ul>
    <h3 class="font-semibold mt-4">5. Your Rights</h3>
    <ul class="list-disc list-inside text-slate-700">
      <li>Request access to your data.</li>
      <li>Request correction or deletion of your data.</li>
      <li>Opt out of marketing communications.</li>
    </ul>
    <h3 class="font-semibold mt-4">6. Data Security</h3>
    <p>We implement industry-standard security measures to protect your data. However, no method of transmission over the internet is 100% secure.</p>
    <h3 class="font-semibold mt-4">7. Contact Us</h3>
    <p>
      If you have any questions, contact us at:
      <br />
      <strong>Yaniva Ventures Private Limited</strong>
      <br />
      Email: info@yanivaventures.com
      <br />
      Phone: +91 91069 30955
    </p>
  `,
  termsAndConditions: `
  <strong>Effective Date:</strong> 1st August, 2024
  <p>Welcome to Yaniva Ventures Private Limited! These Terms and Conditions ("Terms") govern your use of our website and services.</p>

  <h3 class="font-semibold mt-4">1. Acceptance of Terms</h3>
  <p>By accessing or using our website, you agree to these Terms. If you disagree, please discontinue using the site.</p>

  <h3 class="font-semibold mt-4">2. Services Provided</h3>
  <p>We provide a platform for corporate finance advisory, financial insights, and related services. Usage of our platform is subject to compliance with these Terms.</p>

  <h3 class="font-semibold mt-4">3. User Responsibilities</h3>
  <ul class="list-disc list-inside text-slate-700">
    <li>Provide accurate and complete information during registration.</li>
    <li>Use the website for lawful purposes.</li>
    <li>Avoid activities that disrupt website operations or violate intellectual property rights.</li>
  </ul>

  <h3 class="font-semibold mt-4">4. Buyers' Account and Registration Obligations</h3>
  <p>If the Buyers use the Website and register as Buyers, they themselves shall be responsible for maintaining the confidentiality of their details. The Buyers agree that if they provide any information that is untrue, inaccurate, not current or incomplete, Yaniva Ventures has reasonable grounds to suspect that such information is untrue, inaccurate, not current or incomplete, or not in accordance with these Terms and Conditions, Yaniva Ventures shall have the right to indefinitely suspend or terminate or block access of such Buyers’ registration/account and refuse to provide such Buyers with access to the Website.</p>

  <h3 class="font-semibold mt-4">5. Payment Policy</h3>
  <ul class="list-disc list-inside text-slate-700">
    <li>Buyers will have to make payment through any banking mode such as NEFT/RTGS, Internet banking etc. Any mode except cash would be available to make payment.</li>
    <li>At the time of buying the securities, the Buyer will have to make sure that he/she uses their own bank account for making payment for buying securities. Only after receipt of full payment towards the shares, the entire lot shall be transferred to the Buyer.</li>
    <li>Buyers consent to receiving communications via electronic records from Yaniva Ventures periodically and as required.</li>
  </ul>

  <h3 class="font-semibold mt-4">6. Delivery of Shares</h3>
  <p>Buyer will be delivered securities within 72 working hours. Delivery period of securities in Demat form will be the same as mentioned above. Shares will only be transferred to the name of the person from whose account the consideration amount is received.</p>

  <h3 class="font-semibold mt-4">7. Transfer</h3>
  <p>Yaniva Ventures does not take any kind of legal or financial responsibility for the purchased shares in dispute. In case an objection is raised for securities lodged for transfer with the Company, Yaniva Ventures will support clearing objections, if raised within 3 months.</p>

  <h3 class="font-semibold mt-4">8. Cancellation and Refund Policy for Buyer</h3>
  <p>Buyer shall not be entitled to cancel the order once placed for buying the securities after Yaniva gives confirmation. However, the Buyer can cancel any order placed on the platform before Yaniva Ventures confirms the order.</p>

  <h3 class="font-semibold mt-4">9. Disputes Resolution Policy</h3>
  <p>Disputes will be resolved through arbitration in accordance with the Arbitration and Conciliation Act, 1996. The seat of arbitration shall be Delhi, and proceedings will be conducted in English.</p>

  <h3 class="font-semibold mt-4">10. Email Abuse & Threat Policy</h3>
  <p>Yaniva Ventures encourages users to be professional and respectful in communications. Buyers should report any spoof (fake) emails through the 'Contact Us' tab.</p>

  <h3 class="font-semibold mt-4">11. Intellectual Property</h3>
  <p>All content, trademarks, and materials on the website are the property of Yaniva Ventures Private Limited and protected under applicable laws.</p>

  <h3 class="font-semibold mt-4">12. Disclaimer of Liability</h3>
  <p>We do not guarantee the accuracy or completeness of the information on the website. Investment decisions should be made after consulting financial advisors.</p>

  <h3 class="font-semibold mt-4">13. Termination</h3>
  <p>We reserve the right to suspend or terminate access to the website for violations of these Terms.</p>

  <h3 class="font-semibold mt-4">14. Governing Law</h3>
  <p>These Terms are governed by the laws of India. Disputes arising will be subject to the exclusive jurisdiction of courts.</p>

  <h3 class="font-semibold mt-4">15. Changes to Terms</h3>
  <p>We may update these Terms from time to time. Continued use of the website constitutes acceptance of the updated Terms.</p>

  <h3 class="font-semibold mt-4">16. Contact Us</h3>
  <p>
    For queries about these Terms, contact us at:
    <br />
    Yaniva Ventures Private Limited
    <br />
    Email: info@yanivaventures.com
    <br />
    Phone: +91 91069 30955
  </p>
 `,
  disclaimer: `
  <h3 class="font-semibold mt-4 text-slate-800">Regulatory Status and Disclaimer:</h3>
  <p>Yaniva Ventures Private Limited is neither a stock exchange nor authorized by SEBI to solicit investments. Yaniva Ventures Private Limited is a company inter-alia engaged in the online business of trading securities of unlisted companies through its Website. Yaniva Ventures Private Limited has obtained applicable licenses and permission from the appropriate regulatory, as required under various laws for operating the Website. However, Yaniva Ventures Private Limited is neither authorized nor recognized under any law governing the securities market.</p>

  <h3 class="font-semibold mt-4 text-slate-800">Investment Advisory Disclaimer:</h3>
  <p>Yaniva Ventures Private Limited is neither an investment advisor nor acting as an investment advisor. Any statement or information on the website is not a form of investment advisory. Buyers shall not rely on such information and should consult their legal or financial advisors before purchasing securities from Yaniva Ventures Private Limited. Yaniva Ventures Private Limited shall not be liable to compensate for any loss caused to Buyers due to the purchase/sale of securities from Yaniva Ventures Private Limited.</p>

  <h3 class="font-semibold mt-4 text-slate-800">1. Business Operations:</h3>
  <p>Yaniva Ventures Private Limited facilitates the trading of shares through its portfolio. We deal with companies seeking liquidity in their shareholding. Our process involves buyers submitting their purchase requests through our website, and we execute transactions based on share availability and fund status.</p>

  <h3 class="font-semibold mt-4 text-slate-800">2. Trading Scope:</h3>
  <p>We exclusively deal in unlisted securities. Our operations do not include securities actively traded on Nationwide Stock Exchanges, and none of the securities traded through our platform are listed on any regulated stock exchange.</p>

  <h3 class="font-semibold mt-4 text-slate-800">3. Securities Disclaimer:</h3>
  <p>All securities are sold on an "as is where is" basis. Buyers assume full responsibility for any liens, encumbrances, or pledges discovered after purchase. Yaniva Ventures Private Limited bears no liability for such circumstances.</p>

  <h3 class="font-semibold mt-4 text-slate-800">4. Due Diligence:</h3>
  <p>Buyers are responsible for conducting their own due diligence. While we offer due diligence and valuation services, our liability is limited to service deficiencies only when these services are specifically purchased. We bear no responsibility for losses incurred when these services are not utilized.</p>

  <h3 class="font-semibold mt-4 text-slate-800">5. Pricing:</h3>
  <p>All security prices are listed in Indian Rupees, inclusive of applicable taxes.</p>

  <h3 class="font-semibold mt-4 text-slate-800">6. Transaction Rights:</h3>
  <p>Yaniva Ventures Private Limited reserves the right to refuse or cancel any order. Prices may fluctuate based on market demand and supply. Orders cannot be canceled post-execution.</p>

  <h3 class="font-semibold mt-4 text-slate-800">7. KYC Requirements:</h3>
  <p>To ensure security and compliance, all buyers must complete registration with required KYC documentation (Aadhaar card/Driving License/Passport/PAN Card). Using our website indicates acceptance of our terms, conditions, and disclaimers.</p>

  <h3 class="font-semibold mt-4 text-slate-800">8. Advisory Disclaimer:</h3>
  <p>We do not provide legal, investment, or other advisory services. We make no representations regarding the suitability of any agreement for any person or purpose and assume no liability for investment decisions made using our platform.</p>

  <h3 class="font-semibold mt-4 text-slate-800">9. Risk Disclosure:</h3>
  <p>Unlisted securities trading involves substantial risks, including:</p>
  <ul class="list-disc list-inside text-slate-700">
    <li>Potential for complete capital loss</li>
    <li>High speculation levels</li>
    <li>Limited liquidity</li>
    <li>Long-term investment horizon requirement</li>
  </ul>
  <p>Investors should only participate if they can tolerate high risk and don't require liquid investments.</p>

  <h3 class="font-semibold mt-4 text-slate-800">10. Information Accuracy:</h3>
  <p>While our website content is regularly updated using public domain and government sources, we:</p>
  <ul class="list-disc list-inside text-slate-700">
    <li>Provide research reports solely for educational purposes, not as investment advice</li>
    <li>Don't guarantee the accuracy, completeness, or quality of information on the website</li>
    <li>Recommend independent due diligence before any purchase</li>
  </ul>

  <h3 class="font-semibold mt-4 text-slate-800">11. Technical Disclaimer:</h3>
  <p>Yaniva Ventures Private Limited provides no warranty regarding website security from viruses or other destructive elements and assumes no liability for such issues.</p>
  `,
};

const Modal = ({ title, content, isOpen, onClose }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 flex items-center justify-center bg-slate-900/60 backdrop-blur-sm z-50 p-4 animate-fade-in">
      <div className="relative bg-white rounded-3xl w-full max-w-4xl h-[80vh] shadow-2xl overflow-hidden border border-slate-100">
        
        {/* Watermark */}
        <div
          className="absolute inset-0 opacity-5 pointer-events-none"
          style={{
            backgroundImage: `url(${CompanyLogo})`,
            backgroundSize: "contain",
            backgroundRepeat: "no-repeat",
            backgroundPosition: "center",
          }}
        ></div>

        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-slate-100 p-6 flex justify-between items-center z-10">
          <h2 className="text-xl md:text-2xl font-extrabold text-slate-900">{title}</h2>
          <button
            onClick={onClose}
            className="w-9 h-9 flex items-center justify-center rounded-xl bg-slate-50 hover:bg-slate-100 text-slate-500 hover:text-slate-800 transition-colors"
          >
            <X size={20} />
          </button>
        </div>

        {/* Modal Content */}
        <div className="overflow-y-auto h-[calc(80vh-5rem)] p-8">
          <div
            className="prose max-w-none text-slate-600 text-sm md:text-base leading-relaxed space-y-4"
            dangerouslySetInnerHTML={{ __html: content }}
          ></div>
        </div>
      </div>
    </div>
  );
};

const PolicyCard = ({ icon, title, description, onClick }) => (
  <div
    onClick={onClick}
    className="bg-white rounded-3xl p-8 border border-slate-100 shadow-sm hover:shadow-lg hover:-translate-y-1 transition-all duration-300 cursor-pointer flex flex-col items-center text-center group"
  >
    <div className="w-14 h-14 bg-cyan-50/50 text-cyan-600 group-hover:bg-cyan-600 group-hover:text-white rounded-2xl flex items-center justify-center mb-6 transition-all duration-300">
      {icon}
    </div>
    <h3 className="text-lg font-bold text-slate-800 mb-2 group-hover:text-cyan-600 transition-colors">{title}</h3>
    <p className="text-sm text-slate-500 leading-relaxed">{description}</p>
  </div>
);

const PrivacyPolicy = () => {
  useSEO({
    title: "Legal Information, Privacy Policy & Disclaimer | Yaniva Ventures",
    description: "Read the Terms & Conditions, Privacy Policy, and Disclaimers for trading unlisted securities and utilizing Yaniva Ventures services."
  });

  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [popupTitle, setPopupTitle] = useState("");
  const [popupContent, setPopupContent] = useState("");

  const handleOpenPopup = (title, content) => {
    setPopupTitle(title);
    setPopupContent(content);
    setIsPopupOpen(true);
  };

  const handleClosePopup = () => {
    setIsPopupOpen(false);
  };

  const policies = [
    {
      icon: <Shield className="w-6 h-6" />,
      title: "Privacy Policy",
      description: "Learn how we protect and handle your personal information",
      content: TabContent.privacyPolicy
    },
    {
      icon: <Book className="w-6 h-6" />,
      title: "Terms & Conditions",
      description: "Read our terms of service and usage guidelines",
      content: TabContent.termsAndConditions
    },
    {
      icon: <AlertCircle className="w-6 h-6" />,
      title: "Disclaimer",
      description: "Important information about our services and limitations",
      content: TabContent.disclaimer
    }
  ];

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col">
      <NavBar />
      
      <div className="flex-grow container mx-auto px-6 py-16 max-w-7xl flex flex-col justify-center">
        {/* Header */}
        <div className="text-center mb-12">
          <span className="text-sm font-semibold tracking-wider uppercase text-cyan-600 mb-2 block">
            Compliance & Legal
          </span>
          <h1 className="text-3xl md:text-4xl font-extrabold text-slate-900 mb-4">Legal Information</h1>
          <p className="text-slate-500 max-w-2xl mx-auto text-sm md:text-base leading-relaxed">
            Please review our policies carefully to understand how we operate and protect your interests
          </p>
        </div>

        {/* Cards Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto w-full">
          {policies.map((policy) => (
            <PolicyCard
              key={policy.title}
              icon={policy.icon}
              title={policy.title}
              description={policy.description}
              onClick={() => handleOpenPopup(policy.title, policy.content)}
            />
          ))}
        </div>

        {/* Last Updated */}
        <div className="text-center mt-12 text-xs text-slate-400 font-medium">
          Last updated: August 1st, 2024
        </div>
      </div>

      <Modal
        title={popupTitle}
        content={popupContent}
        isOpen={isPopupOpen}
        onClose={handleClosePopup}
      />

      <Clients />
    </div>
  );
};

export default PrivacyPolicy;